// Analytics: everything that can be compared, built from the logged-in venue's locations.
// A "subject" is one line on the charts and one row in the table: a retail store, a concession stand
// (its sections averaged) or one section, a whole gates location or one gate, a whole restrooms
// location or one restroom. Each lists the measurements it has.

// Order used everywhere a subject's measurements are listed (chart options, table options)
export const METRICS = {
  queue: { key: 'queue', label: 'Queue', unit: 'min' },
  entries: { key: 'entries', label: 'Entries', unit: 'entries' },
  exits: { key: 'exits', label: 'Exits', unit: 'exits' },
  occupancy: { key: 'occupancy', label: 'Occupancy', unit: 'people' },
  count: { key: 'count', label: 'People count', unit: 'people' },
  transaction: { key: 'transaction', label: 'Transaction time', unit: 'sec' },
};
export const METRIC_ORDER = ['queue', 'entries', 'exits', 'occupancy', 'count', 'transaction'];

export const VERTICAL_LABELS = {
  retail: 'Retail',
  concession: 'Concessions',
  gates: 'Gates',
  facilities: 'Restrooms',
};

const gateHasQueue = (gate) => !!gate.multiSensor?.queue_time || (gate.queueIds || []).length > 0;
const restroomHasQueue = (restroom) => !!restroom.multiSensor?.queue_time;

// A location's logo bundled with the app (logoPath), or null if it has none or the file is missing
const bundledLogo = (location) => {
  if (!location.logoPath) return null;
  try {
    return require(`../../clients/${location.logoPath}`);
  } catch (e) {
    return null;
  }
};
const retailLogo = bundledLogo;


const ordered = (metrics) => METRIC_ORDER.filter((m) => metrics.includes(m));

const retailSubject = (location) => {
  const metrics = [];
  if (location.maxOccupancy > 0) metrics.push('occupancy');
  if ((location.doorCounterSensorIds || []).length > 0) metrics.push('entries');
  if ((location.queueTimeSensorIds || []).length > 0) metrics.push('queue');
  if (location.transactionMeasurement) metrics.push('transaction');
  return {
    key: `retail-${location.id}`,
    vertical: 'retail',
    kind: 'store',
    location,
    unitId: null,
    name: location.name,
    parentName: null,
    sub: 'Retail store',
    image: retailLogo(location),
    metrics: ordered(metrics),
  };
};

const concessionMetrics = (location) => (location.sensorDataType === 'count' ? ['count'] : ['queue', 'transaction']);

const concessionSubjects = (location) => {
  const sections = location.sections || [];
  const stand = {
    key: `concession-${location.id}`,
    vertical: 'concession',
    kind: 'stand',
    location,
    unitId: null,
    name: location.name,
    parentName: null,
    sub: sections.length > 1 ? `All ${sections.length} sections, averaged` : sections.length === 1 ? `Section ${sections[0].sectionId}` : 'Concession stand',
    image: location.logoMobileURL || null,
    metrics: concessionMetrics(location),
  };
  // A stand with one section: picking the stand already is that section
  const children = sections.length > 1
    ? sections.map((s) => ({
        key: `concession-${location.id}-${s.sectionId}`,
        vertical: 'concession',
        kind: 'section',
        location,
        unitId: String(s.sectionId),
        name: `${location.name} · Section ${s.sectionId}`,
        shortName: `Section ${s.sectionId}`,
        parentName: location.name,
        sub: s.label || null,
        image: location.logoMobileURL || null,
        metrics: concessionMetrics(location),
      }))
    : [];
  return { ...stand, children };
};

const gatesSubjects = (location) => {
  const gates = location.gates || [];
  const anyQueue = gates.some(gateHasQueue);
  const whole = {
    key: `gates-${location.id}`,
    vertical: 'gates',
    kind: 'gates',
    location,
    unitId: null,
    name: gates.length > 1 ? `${location.name} (all gates)` : location.name,
    parentName: null,
    sub: `${gates.length} gate${gates.length === 1 ? '' : 's'} combined`,
    image: null,
    metrics: ordered(['entries', 'exits', ...(anyQueue ? ['queue'] : [])]),
  };
  const children = gates.map((g) => ({
    key: `gates-${location.id}-${g.gateId}`,
    vertical: 'gates',
    kind: 'gate',
    location,
    unitId: String(g.gateId),
    name: g.label || `Gate ${g.gateId}`,
    shortName: g.label || `Gate ${g.gateId}`,
    parentName: location.name,
    sub: gateHasQueue(g) ? 'Entries, exits and queue' : 'Entries and exits',
    image: g.imageURL || null,
    metrics: ordered(['entries', 'exits', ...(gateHasQueue(g) ? ['queue'] : [])]),
  }));
  return { ...whole, children };
};

const facilitiesSubjects = (location) => {
  const restrooms = location.restrooms || [];
  const anyQueue = restrooms.some(restroomHasQueue);
  const whole = {
    key: `facilities-${location.id}`,
    vertical: 'facilities',
    kind: 'restrooms',
    location,
    unitId: null,
    name: restrooms.length > 1 ? `${location.name} (all restrooms)` : location.name,
    parentName: null,
    sub: `${restrooms.length} restroom${restrooms.length === 1 ? '' : 's'} combined`,
    image: null,
    metrics: ordered(['entries', 'exits', ...(anyQueue ? ['queue'] : [])]),
  };
  const children = restrooms.map((r) => {
    const label = String(r.label || '').trim() || `Restroom ${r.restroomId}`;
    const name = r.sectionId != null ? `${label}, Section ${r.sectionId}` : label;
    return {
      key: `facilities-${location.id}-${r.restroomId}`,
      vertical: 'facilities',
      kind: 'restroom',
      location,
      unitId: String(r.restroomId),
      name,
      shortName: name,
      parentName: location.name,
      sub: restroomHasQueue(r) ? 'Entries, exits and queue' : 'Entries and exits',
      image: r.imageURL || null,
      metrics: ordered(['entries', 'exits', ...(restroomHasQueue(r) ? ['queue'] : [])]),
    };
  });
  return { ...whole, children };
};

// Picker groups, in the order the Locations page shows them. Subjects with no measurements are left out.
export const buildCatalog = (authLocationData) => {
  const data = authLocationData || {};
  const keep = (s) => s.metrics.length > 0;
  const withChildren = (s) => ({ ...s, children: (s.children || []).filter(keep) });
  const groups = [
    { vertical: 'retail', items: (data.retail || []).map(retailSubject).filter(keep) },
    { vertical: 'concession', items: (data.concessions || []).map(concessionSubjects).filter(keep).map(withChildren) },
    { vertical: 'gates', items: (data.gates || []).filter((l) => (l.gates || []).length).map(gatesSubjects).filter(keep).map(withChildren) },
    { vertical: 'facilities', items: (data.facilities || []).filter((l) => (l.restrooms || []).length).map(facilitiesSubjects).filter(keep).map(withChildren) },
  ];
  return groups
    .filter((g) => g.items.length > 0)
    .map((g) => ({ ...g, label: VERTICAL_LABELS[g.vertical] }));
};

// Every subject (parents and children) by key
export const subjectIndex = (catalog) => {
  const map = new Map();
  catalog.forEach((g) => g.items.forEach((s) => {
    map.set(s.key, s);
    (s.children || []).forEach((c) => map.set(c.key, c));
  }));
  return map;
};

// The location whose schedule the page uses (the first one, as before)
export const scheduleLocation = (authLocationData) => {
  const data = authLocationData || {};
  const pick = (list, vertical) => (list && list.length ? { location: list[0], vertical } : null);
  return pick(data.retail, 'retail') || pick(data.concessions, 'concession') || pick(data.gates, 'gates') || pick(data.facilities, 'facilities');
};
